import {
  ELIGIBILITY_RULE_TYPES,
  ELIGIBILITY_STATUS,
  NOT_EVALUATED_SOURCE,
  RULE_OUTCOMES,
  RULE_POLICY,
  VEHICLE_FACT_SOURCE,
  type ProgramMoveEligibilityRule,
  type ProgramQuote,
  type VehicleFactSource,
} from '@hopdrive/sdk-ar';
import {
  ELIGIBILITY_ROW_COPY,
  LOCATION_FIELDS,
  LOCATION_ROW_KEY,
  PROGRAM_CHECKED_RULE_KEYS,
  PROGRAM_CHECKS,
  eligibilityRowLabel,
  laterDetail,
  type EligibilityRow,
  type EligibilityRowState,
} from '@hopdrive/feature-program-claims';

/**
 * What the planner's vehicle means for a program quote: whether the facts the
 * contract's create-time eligibility reads are all in, and each of the
 * contract's rules as a row the dealer can read ("Genesis vehicle", "Make is
 * Genesi") and act on before creating the move. The rows, their wording and
 * the program's own checks are shared with the claims list, which shows the
 * same checklist from a claim's recorded verdicts
 * (@hopdrive/feature-program-claims); the planner's own words are in ROW_COPY
 * and SUMMARY_COPY. Read off the quote's
 * rules and their evaluation, so it follows each contract rather than assuming
 * every program wants a VIN. Pure, so it is unit-testable; the hook, the
 * coverage bar and the eligibility checklist render over it.
 */

export type VehicleFact = 'vin' | 'make' | 'year';

// The source sdk-ar's evaluator records when a vehicle rule had nothing to read
// (evaluate-program-move-eligibility.ts). Until each is gone the quote is
// assuming the vehicle eligible, and the VIN decode can still flip it.
const MISSING_FACT_SOURCES: ReadonlyMap<string, VehicleFact> = new Map([
  ['vin_incomplete', 'vin'],
  ['vehicle_make_missing', 'make'],
  ['vehicle_year_missing', 'year'],
]);

/** The vehicle facts the quote is still assuming, in the order the contract evaluated them. */
export function missingVehicleFacts(quote: ProgramQuote | null | undefined): VehicleFact[] {
  const missing = (quote?.eligibility.evaluationResult ?? []).flatMap(rule => {
    const fact = rule.outcome === RULE_OUTCOMES.ASSUME ? MISSING_FACT_SOURCES.get(rule.source) : undefined;
    return fact ? [fact] : [];
  });
  return [...new Set(missing)];
}

/** True once the quote is in and nothing the planner's vehicle supplies is still assumed. */
export function isVehicleReady(quote: ProgramQuote | null | undefined): boolean {
  return quote != null && missingVehicleFacts(quote).length === 0;
}

/**
 * What to enter before the estimate can say anything ("Enter the VIN to check
 * eligibility."). A VIN decodes the make and year, so a missing VIN is the one
 * thing to ask for.
 */
export function vehicleFactsPrompt(missing: ReadonlyArray<VehicleFact>, toWhat = "see this move's cost and coverage"): string {
  const suffix = ` to ${toWhat}.`;
  if (missing.includes('vin')) return `Enter the VIN${suffix}`;
  const facts = [missing.includes('make') ? 'make' : null, missing.includes('year') ? 'model year' : null].filter(Boolean);
  return facts.length ? `Enter the vehicle's ${facts.join(' and ')}${suffix}` : `Enter the vehicle${suffix}`;
}

/** The planner values a row quotes back to the dealer: the ones the quote judged. */
export interface PlannerVehicle {
  make: string | null;
  year: number | null;
  /** Where `make` and `year` came from. Absent reads as typed into the form. */
  factSource?: VehicleFactSource | null;
  /** The make typed into the form, which the program sets aside when the VIN decodes one. */
  enteredMake?: string | null;
}

const fromVin = (vehicle: PlannerVehicle): boolean => vehicle.factSource === VEHICLE_FACT_SOURCE.VIN_DECODE;

/**
 * The VIN decoded one make and the dealer typed another. The program goes by the VIN, so the
 * make row says so, or a dealer who typed "Genesis" can't see why it doesn't count.
 */
const enteredMakeSetAside = (vehicle: PlannerVehicle): boolean =>
  fromVin(vehicle) &&
  !!vehicle.make &&
  !!vehicle.enteredMake?.trim() &&
  vehicle.enteredMake.trim().toLowerCase() !== vehicle.make.trim().toLowerCase();

/** The shared words, and the ones only the planner says: while the vehicle is still coming in, and about its VIN decode. */
const ROW_COPY = {
  ...ELIGIBILITY_ROW_COPY,
  vinNeeded: 'Before pickup',
  waitingOnVin: 'Once the VIN is in',
  waitingOnMake: 'Enter the make',
  waitingOnYear: 'Enter the model year',
  makeFromVinFailed: (decoded: string) => `VIN is ${/^[aeiou]/i.test(decoded) ? 'an' : 'a'} ${decoded}`,
  makeFromVinNote: 'The program reads the make from the VIN, not the make entered.',
};

/** Failure details that only make sense beside their label ("RO number: missing"). */
const GENERIC_FAILURES: ReadonlySet<string> = new Set([ROW_COPY.notMet, ROW_COPY.roMissing]);

/** The rules whose facts the planner's vehicle section supplies. */
const VEHICLE_RULE_TYPES: ReadonlySet<string> = new Set([
  ELIGIBILITY_RULE_TYPES.MAKE_IN,
  ELIGIBILITY_RULE_TYPES.MODEL_YEAR_MAX_AGE,
  ELIGIBILITY_RULE_TYPES.VIN_COMPLETE,
]);

/** Why a rule failed, quoting the planner's value where the rule read one. */
function failedDetail(rule: ProgramMoveEligibilityRule, vehicle: PlannerVehicle): string {
  switch (rule.type) {
    case ELIGIBILITY_RULE_TYPES.MAKE_IN:
      if (!vehicle.make) return ROW_COPY.notMet;
      return fromVin(vehicle) ? ROW_COPY.makeFromVinFailed(vehicle.make) : ROW_COPY.makeFailed(vehicle.make);
    case ELIGIBILITY_RULE_TYPES.MODEL_YEAR_MAX_AGE:
      return ROW_COPY.ageFailed(vehicle.year);
    case ELIGIBILITY_RULE_TYPES.FIELD_PRESENT:
      return ROW_COPY.roMissing;
    default:
      return ROW_COPY.notMet;
  }
}

function waitingDetail(rule: ProgramMoveEligibilityRule, missing: ReadonlyArray<VehicleFact>): string {
  if (rule.type === ELIGIBILITY_RULE_TYPES.VIN_COMPLETE) return ROW_COPY.vinNeeded;
  if (missing.includes('vin')) return ROW_COPY.waitingOnVin;
  return rule.type === ELIGIBILITY_RULE_TYPES.MAKE_IN ? ROW_COPY.waitingOnMake : ROW_COPY.waitingOnYear;
}

/**
 * One row per rule the quote evaluated, in the contract's order, with the
 * location and timing signals folded into one row where the first of them sits.
 * A rule of a type the evaluator doesn't know never counts, so it reads as not
 * checked.
 */
export function eligibilityChecklist(quote: ProgramQuote | null | undefined, vehicle: PlannerVehicle): EligibilityRow[] {
  if (!quote) return [];
  const missing = missingVehicleFacts(quote);
  const ready = missing.length === 0;
  const rows: EligibilityRow[] = [];
  for (const rule of quote.eligibilityRules ?? []) {
    if (rule.field && LOCATION_FIELDS.has(rule.field)) {
      if (!rows.some(row => row.key === LOCATION_ROW_KEY)) {
        rows.push({ key: LOCATION_ROW_KEY, state: 'later', label: ROW_COPY.locationLabel, detail: ROW_COPY.locationDetail });
      }
      continue;
    }
    if (PROGRAM_CHECKED_RULE_KEYS.has(rule.key)) continue;
    const evaluation = quote.eligibility.evaluationResult.find(result => result.ruleKey === rule.key);
    const info = rule.type === ELIGIBILITY_RULE_TYPES.MAKE_IN && enteredMakeSetAside(vehicle) ? { info: ROW_COPY.makeFromVinNote } : null;
    const row = (state: EligibilityRowState, detail: string | null) =>
      rows.push({ key: rule.key, state, label: eligibilityRowLabel(rule), detail, ...info });
    // The model year can rule a vehicle out, never in, so it is a row only when it does.
    const failed = evaluation?.outcome === RULE_OUTCOMES.FAIL && (ready || !VEHICLE_RULE_TYPES.has(rule.type));
    if (rule.type === ELIGIBILITY_RULE_TYPES.MODEL_YEAR_MAX_AGE && !failed) continue;

    if (!evaluation || evaluation.source === NOT_EVALUATED_SOURCE || rule.policy === RULE_POLICY.ALWAYS_ASSUME) {
      row('assumed', evaluation ? ROW_COPY.assumed : ROW_COPY.unknownType);
    } else if (!ready && VEHICLE_RULE_TYPES.has(rule.type)) row('waiting', waitingDetail(rule, missing));
    else if (evaluation.outcome === RULE_OUTCOMES.PASS) row('passed', null);
    else if (failed) row('failed', failedDetail(rule, vehicle));
    else row('later', laterDetail(evaluation.timing));
  }
  return rows;
}

/** The words the collapsed summary line carries. */
export const SUMMARY_COPY = {
  eligible: 'Eligible for program coverage',
  eligiblePendingLater: 'Eligible if the remaining checks pass',
  ineligible: 'Not eligible for program coverage',
  passed: (n: number) => `${n} ${n === 1 ? 'check' : 'checks'} passed`,
  later: (n: number) => `${n} checked later`,
  assumed: (n: number) => `${n} assumed`,
};

/**
 * The short hint beside the collapsed status: the first failed rule's reason
 * ("Make is Genesi"), or the counts of where the rules stand ("3 checks passed · 2 checked later").
 */
export function eligibilitySummaryHint(rows: ReadonlyArray<EligibilityRow>): string | null {
  const failed = rows.find(row => row.state === 'failed');
  if (failed) return failed.detail && !GENERIC_FAILURES.has(failed.detail) ? failed.detail : `${failed.label}: ${(failed.detail ?? ROW_COPY.notMet).toLowerCase()}`;
  const count = (state: EligibilityRowState) => rows.filter(row => row.state === state).length;
  const parts = [
    count('passed') ? SUMMARY_COPY.passed(count('passed')) : null,
    count('later') ? SUMMARY_COPY.later(count('later')) : null,
    count('assumed') ? SUMMARY_COPY.assumed(count('assumed')) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

export type EligibilityStatus = 'waiting' | 'eligible' | 'ineligible';

/** What the coverage bar and the summary both show: the verdict, its hint, and the rule rows. */
export interface EligibilityView {
  status: EligibilityStatus;
  statusText: string;
  hint: string | null;
  rows: EligibilityRow[];
  /** What the program verifies after filing, listed under the rows. */
  programChecks: ReadonlyArray<EligibilityRow>;
}

/**
 * The verdict as the dealer reads it. Until the vehicle is in it asks for what
 * is missing; once it is, a vehicle that passes everything checkable now is
 * eligible on the condition that the rules checked later (mileage, RO, ...)
 * pass too, and says so.
 */
export function eligibilityView(
  quote: ProgramQuote,
  vehicle: PlannerVehicle,
  missing: ReadonlyArray<VehicleFact>,
): EligibilityView {
  const rows = eligibilityChecklist(quote, vehicle);
  if (missing.length > 0) {
    return { status: 'waiting', statusText: vehicleFactsPrompt(missing, 'check eligibility'), hint: null, rows, programChecks: PROGRAM_CHECKS };
  }
  const hint = eligibilitySummaryHint(rows);
  if (quote.eligibility.eligibilityStatus === ELIGIBILITY_STATUS.INELIGIBLE) {
    return { status: 'ineligible', statusText: SUMMARY_COPY.ineligible, hint, rows, programChecks: PROGRAM_CHECKS };
  }
  const pendingLater = rows.some(row => row.state === 'later');
  return { status: 'eligible', statusText: pendingLater ? SUMMARY_COPY.eligiblePendingLater : SUMMARY_COPY.eligible, hint, rows, programChecks: PROGRAM_CHECKS };
}
